import { fmtCount, fmtHours } from '../StreamIngest/vocabulary'
import { fmtShare, pushTotals, share } from '../Loop/vocabulary'

// The automatic summary: each row's facts, and the one sentence they make
// (README.md, "Making the summary smarter"). A row's panel and the Overview's
// phone summary both read these, so a sentence is written once.
//
// A sentence is `{ status, text }`: `status` is `error`, `warning`, `ok` or
// `idle`, and colours the row's edge; `text` is one plain sentence. Each
// verdict is a list of checks, worst first, and the first that holds is the
// sentence. Rows are judged by loss, never by a rate or a latency: nothing
// sets a target for those yet, so a rate is said, not judged. Every number in
// a sentence is one the reads carry; nothing is estimated.

// "today", or "in the last 7 days": `1d` is today so far (UTC).
export const windowPhrase = (days) =>
  days === 1 ? 'today' : `in the last ${days} days`

const plural = (n, one, many) => (n === 1 ? one : many)

// ---- the pipeline: audio → quotes -----------------------------------------

// What the pipeline's sentence and its stages both read off the latency and
// capture answers: the fleet's microphones, today's capture, and the spans
// lost, stuck, stalled and still uploading in the window. Stalled is a span
// declared with no audio arrived past the server's patience (`in_flight`).
// Lost is split by what the install that recorded it did next (`lost`): only
// one whose install came back and still didn't send it is a fault; the rest
// are a phone not opened since, a new install, or no word in a week. An API
// from before the split counts every loss as a fault.
export const pipelineFacts = ({ payload, capture, totals }) => {
  const today = capture?.days?.[0] || null
  const inFlight = payload?.in_flight || null
  const lostBy = payload?.lost || null
  const lost = (totals.nothing_received || 0) + (totals.partial_upload || 0)
  return {
    fleet: capture?.fleet || null,
    me: capture?.user || null,
    today,
    lost: lostBy ? lostBy.came_back.spans : lost,
    lostSplit: Boolean(lostBy),
    lostHours: lostBy ? lostBy.came_back.hours : totals.unsealed_hours,
    notUploaded: lostBy
      ? {
          waiting: lostBy.waiting.spans,
          oldestWaitingH: lostBy.oldest_waiting_age_h,
          newInstall: lostBy.new_install.spans,
          gone: lostBy.gone.spans,
          goneAfterDays: lostBy.gone_after_days,
        }
      : null,
    stuck: totals.stuck_declared || 0,
    inFlight: totals.in_flight || 0,
    stalled: inFlight?.stalled || 0,
    oldestStalledH: inFlight?.oldest_stalled_age_h ?? null,
    belowBar: today?.coverage?.users_below_bar || 0,
    horizon: payload?.horizon_hours,
  }
}

// `reads` is `usePipelineReads`'s answer; `user` the account a page is
// narrowed to.
export const pipelineVerdict = (
  { payload, capture, totals },
  { user } = {}
) => {
  if (!payload || !capture) return { status: 'idle', text: 'Loading…' }
  const {
    fleet,
    lost,
    lostSplit,
    stuck,
    stalled,
    oldestStalledH,
    belowBar,
    horizon,
  } = pipelineFacts({
    payload,
    capture,
    totals,
  })
  if (totals.declared === 0)
    return {
      status: 'idle',
      text: user
        ? 'This account has streamed nothing in this window.'
        : 'Nothing has streamed in this window.',
    }
  if (fleet && fleet.silent > 0)
    return {
      status: 'error',
      text: `${fleet.silent} phone${plural(
        fleet.silent,
        ' is',
        's are'
      )} recording but nothing is arriving from ${plural(
        fleet.silent,
        'it',
        'them'
      )}.`,
    }
  if (lost > 0)
    return {
      status: 'error',
      text: `${lost} span${plural(lost, ' was', 's were')} lost: ${
        lostSplit
          ? `the phone that recorded ${plural(
              lost,
              'it',
              'them'
            )} has been back since and never sent the audio`
          : 'declared, and the audio never arrived'
      }. A late upload can still save ${plural(lost, 'it', 'them')}.`,
    }
  if (stuck > 0)
    return {
      status: 'warning',
      text: `${stuck} span${plural(stuck, ' is', 's are')} past the ${fmtHours(
        horizon
      )} limit without all ${plural(
        stuck,
        'its',
        'their'
      )} audio: the next sweep writes ${plural(
        stuck,
        'it',
        'them'
      )} off as lost.`,
    }
  if (stalled > 0)
    return {
      status: 'warning',
      text: `${stalled} span${plural(
        stalled,
        ' has',
        's have'
      )} had no audio arrive since ${plural(
        stalled,
        'it was',
        'they were'
      )} declared, the oldest ${fmtHours(oldestStalledH)} ago.`,
    }
  if (belowBar > 0)
    return {
      status: 'warning',
      text: `${belowBar} account${plural(
        belowBar,
        ' has',
        's have'
      )} holes in the audio we hold today.`,
    }
  return { status: 'ok', text: 'Audio is flowing.' }
}

// ---- the loop: quotes → people --------------------------------------------

// Sums a window's per-day outcome counts (`[{ day, counts }]`) into one map.
const sumCounts = (days = []) =>
  days.reduce((sum, { counts }) => {
    Object.entries(counts || {}).forEach(([key, n]) => {
      if (typeof n === 'number') sum[key] = (sum[key] || 0) + n
    })
    return sum
  }, {})

// The friend pushes by outcome: sent, failed (Braze refused or never
// answered), lost (the task or the send never ran, settled by the sweep), and
// dropped on purpose by the send-time recheck.
const socialTotals = (rows = []) =>
  rows.reduce(
    (sum, { kind, state, outcome, count }) => {
      if (state === 'sent') sum.sent[kind] = (sum.sent[kind] || 0) + count
      else if (state === 'dropped' && outcome === 'send_failed')
        sum.failed += count
      else if (state === 'dropped' && outcome === 'lost') sum.lost += count
      else if (state === 'dropped') sum.dropped += count
      return sum
    },
    { sent: {}, failed: 0, lost: 0, dropped: 0 }
  )

// What the loop's sentence and its stages both read: the delivery answer
// (Mongo: each quote push's outcome, each friend push sent or dropped) and the
// pushes answer (the event table: what each push led to), summed over the
// window. `outcomes` is null until the event table answers. An API from
// before the friend pushes were counted per send carries only the held rows.
export const loopFacts = ({ delivery, pushes }) => {
  const quotes = sumCounts(delivery?.quotes)
  const social = socialTotals(
    delivery?.friend_pushes ?? delivery?.friends?.days
  )
  const pushed = quotes.pushed || 0
  return {
    quotes,
    pushed,
    friendQuotesSent: social.sent.friend_quotes || 0,
    commentsSent: social.sent.comment || 0,
    requestsSent: social.sent.friend_request || 0,
    acceptedSent: social.sent.friend_accepted || 0,
    sent: Object.values(social.sent).reduce((sum, n) => sum + n, pushed),
    failed: (quotes.push_failed || 0) + social.failed,
    lost: social.lost,
    dropped: social.dropped,
    pastDue: Object.values(delivery?.friends?.held_past_due || {}).reduce(
      (sum, n) => sum + n,
      0
    ),
    outcomes: pushes ? pushTotals(pushes.kinds) : null,
  }
}

// The event table's answer once it is the window's, `null` while it is read:
// the loop never mixes one window's delivery with another's outcomes.
export const currentPushes = ({ pushes, pushesLoading }) =>
  pushesLoading ? null : pushes

// `reads` is `useLoopReads`'s answer; `fleetOnly` a page narrowed to one
// account, which the loop's reads, fleet-wide, cannot follow.
export const loopVerdict = (reads, { fleetOnly = false } = {}) => {
  const { delivery, deliveryError, shownDays: days } = reads
  if (fleetOnly)
    return {
      status: 'idle',
      text: 'The loop is read for the whole fleet: clear the account to see it.',
    }
  if (deliveryError)
    return { status: 'idle', text: 'The pushes could not be read.' }
  if (!delivery) return { status: 'idle', text: 'Loading…' }
  const { sent, failed, lost, pastDue, outcomes } = loopFacts({
    delivery,
    pushes: currentPushes(reads),
  })
  if (failed > 0)
    return {
      status: 'error',
      text: `${fmtCount(failed)} push${plural(
        failed,
        '',
        'es'
      )} failed: Braze refused ${plural(
        failed,
        'it',
        'them'
      )} or never answered.`,
    }
  if (lost > 0)
    return {
      status: 'error',
      text: `${fmtCount(lost)} friend push${plural(
        lost,
        ' was',
        'es were'
      )} lost: a task or a send never ran.`,
    }
  if (pastDue > 0)
    return {
      status: 'warning',
      text: `${fmtCount(pastDue)} friend push${plural(
        pastDue,
        ' is',
        'es are'
      )} past due: a task or a send was lost.`,
    }
  if (sent === 0)
    return { status: 'idle', text: `No push went out ${windowPhrase(days)}.` }
  return {
    status: 'ok',
    text: `${fmtCount(sent)} push${plural(
      sent,
      '',
      'es'
    )} went out ${windowPhrase(days)}${reachOf(outcomes)}`,
  }
}

// What the pushes led to, as the sentence's second half, said only where it
// says something: nothing until the event table answers; none to a phone that
// reports, or none visited, in words rather than a 0%. A visit is a floor on
// the taps, so a share of them is "at least".
const reachOf = (outcomes) => {
  if (!outcomes) return '.'
  if (outcomes.counted === 0) return '; none yet to a phone that reports.'
  if (outcomes.visited === 0)
    return `; none of the ${fmtCount(
      outcomes.counted
    )} to a reporting phone has led to a visit yet.`
  return `; at least ${fmtShare(
    share(outcomes.visited, outcomes.counted)
  )} of those to a reporting phone led to a visit.`
}
